import React from "react";
import styled from "styled-components";
import { WizardStep } from "./WizardStep";
import { Icon } from "../Icon";

export type Step = {
  title: React.ReactNode;
};

export type Props = {
  /**
   * The steps to display in the wizard.
   */
  steps: Step[];

  /**
   * The currently active step.
   */
  currentStep: number;

  /**
   * The orientation of the steps.
   * @default "horizontal"
   */
  orientation?: "horizontal" | "vertical";

  /**
   * A callback that is called when a _previous_ step is selected.
   * If the callback is not provided, the steps will not be clickable.
   */
  onSelect?: (index: number) => void;
};

const Container = styled.div<{ $orientation: "horizontal" | "vertical" }>`
  display: flex;
  justify-content: center;
  align-items: ${(props) => (props.$orientation === "vertical" ? "flex-start" : "center")};
  flex-direction: ${(props) => (props.$orientation === "vertical" ? "column" : "row")};
  gap: 24px;
`;

const indexToState = (index: number, currentStep: number) => {
  if (index < currentStep) {
    return "complete";
  } else if (index === currentStep) {
    return "active";
  } else {
    return "inactive";
  }
};

const indexToIcon = (index: number, currentStep: number) => {
  if (index < currentStep) {
    return <Icon icon="check" />;
  } else {
    return index + 1;
  }
};

export const WizardSteps = (props: Props) => {
  const { steps, currentStep, onSelect, orientation = "horizontal" } = props;

  return (
    <Container $orientation={orientation}>
      {steps.map((step, index) => {
        const state = indexToState(index, currentStep);
        const icon = indexToIcon(index, currentStep);

        return <WizardStep key={index} id={index} icon={icon} title={step.title} state={state} onSelect={onSelect} />;
      })}
    </Container>
  );
};
